<script setup lang="ts">
import { computed } from 'vue'

/**
 * Toast - 토스트 메시지 컴포넌트
 * useToast composable과 함께 사용
 */

export interface ToastItem {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
  duration?: number
}

interface Props {
  /** 토스트 목록 */
  toasts: ToastItem[]
  /** 위치 */
  position?: 'top' | 'bottom'
}

const props = withDefaults(defineProps<Props>(), {
  position: 'top'
})

const emit = defineEmits<{
  remove: [id: string]
}>()

const containerClasses = computed(() => [
  'toast-container',
  `toast-container-${props.position}`
])

const getToastClasses = (type: ToastItem['type']) => [
  'toast',
  `toast-${type}`
]

const getIcon = (type: ToastItem['type']) => {
  switch (type) {
    case 'success':
      return `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.666 5L7.49935 14.1667L3.33268 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
    case 'error':
      return `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
    case 'warning':
      return `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 6V11M10 14H10.01M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
    default: // info
      return `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 10V14M10 6H10.01M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
  }
}

const handleClose = (id: string) => {
  emit('remove', id)
}
</script>

<template>
  <Teleport to="body">
    <div :class="containerClasses">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="getToastClasses(toast.type)"
        >
          <div class="toast-icon" v-html="getIcon(toast.type)" />
          <p class="toast-message">{{ toast.message }}</p>
          <button
            class="toast-close"
            type="button"
            aria-label="닫기"
            @click="handleClose(toast.id)"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-container {
  @apply fixed left-1/2 -translate-x-1/2 z-[9999];
  @apply flex flex-col gap-2;
  @apply w-full max-w-sm px-4;
  @apply pointer-events-none;
}

/* E26(2026-10-13): 화면 맨 위(top-4)라 상단 헤더·뒤로가기를 가렸다 — 헤더 아래로
   (실측: 모바일 고정 헤더 하단 114px · PC GNB 하단 96px) */
.toast-container-top {
  top: calc(env(safe-area-inset-top, 0px) + 122px);
}
@media (min-width: 1024px) {
  .toast-container-top {
    top: 104px;
  }
}

.toast-container-bottom {
  @apply bottom-4;
}

.toast {
  @apply flex items-center gap-3;
  @apply px-4 py-3 rounded-lg shadow-lg;
  @apply pointer-events-auto;
  @apply backdrop-blur-sm;
}

.toast-success {
  @apply bg-[#22C55E]/90 text-white;
}

.toast-error {
  @apply bg-[#FE3C3C]/90 text-white;
}

.toast-warning {
  @apply bg-yellow-500/90 text-white;
}

.toast-info {
  @apply bg-blue-500/90 text-white;
}

.toast-icon {
  @apply flex-shrink-0;
}

.toast-message {
  @apply flex-1 text-sm font-medium;
  /* E18: alert 에서 옮긴 여러 줄 안내(«성공 n명 / 실패 n명\n…») 줄바꿈 유지 */
  white-space: pre-line;
}

.toast-close {
  @apply flex-shrink-0 p-1 rounded;
  @apply hover:bg-white/20 active:bg-white/30;
  @apply transition-colors duration-200;
}

/* 트랜지션 */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateY(-100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
